import React, { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Card, Container, Stack, Typography, Alert, Skeleton } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useLocation } from 'react-router';
import { useSnackbar } from 'notistack';
import axios from '../../../../utils/axios';
import Page from '../../../../components/Page';
import useSettings from '../../../../hooks/useSettings';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import { FormProvider, RHFTextField } from '../../../../components/hook-form';

const HH_MM_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

const normalizeTimeValue = (value) => {
  if (!value || typeof value !== 'string') return value;
  return value.slice(0, 5);
};

const validationSchema = Yup.object().shape({
  closeOnlyTime: Yup.string()
    .required('Close only time is required')
    .matches(HH_MM_REGEX, 'Must be HH:mm'),
  limitCutoffTime: Yup.string()
    .required('Limit cutoff time is required')
    .matches(HH_MM_REGEX, 'Must be HH:mm')
    .test(
      'after-close-only',
      'Must be greater than or equal to close only time',
      function validateLimitCutoff(value) {
        const { closeOnlyTime } = this.parent;
        if (!value || !closeOnlyTime) return true;
        return value >= closeOnlyTime;
      }
    ),
  cancelPendingLimitsTime: Yup.string()
    .required('Cancel pending limits time is required')
    .matches(HH_MM_REGEX, 'Must be HH:mm')
    .test(
      'after-limit-cutoff',
      'Must be greater than or equal to limit cutoff time',
      function validateCancelTime(value) {
        const { limitCutoffTime } = this.parent;
        if (!value || !limitCutoffTime) return true;
        return value >= limitCutoffTime;
      }
    ),
});

const defaultValues = {
  closeOnlyTime: '15:20',
  limitCutoffTime: '15:21',
  cancelPendingLimitsTime: '15:21',
};

const NseMarketTimingSetting = () => {
  const { themeStretch } = useSettings();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  const { reset, handleSubmit, formState } = methods;

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/segement/nse-market-timing');
      const config = response.data?.config || {};
      reset({
        closeOnlyTime: normalizeTimeValue(config.closeOnlyTime) || defaultValues.closeOnlyTime,
        limitCutoffTime: normalizeTimeValue(config.limitCutoffTime) || defaultValues.limitCutoffTime,
        cancelPendingLimitsTime:
          normalizeTimeValue(config.cancelPendingLimitsTime) || defaultValues.cancelPendingLimitsTime,
      });
    } catch (error) {
      enqueueSnackbar(error.message || 'Failed to load NSE market timing', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, reset]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        closeOnlyTime: normalizeTimeValue(data.closeOnlyTime),
        limitCutoffTime: normalizeTimeValue(data.limitCutoffTime),
        cancelPendingLimitsTime: normalizeTimeValue(data.cancelPendingLimitsTime),
      };
      const response = await axios.put('/segement/nse-market-timing', payload);
      enqueueSnackbar(response.data.message || 'NSE market timing updated');
      if (response.data.config) {
        reset({
          closeOnlyTime: normalizeTimeValue(response.data.config.closeOnlyTime),
          limitCutoffTime: normalizeTimeValue(response.data.config.limitCutoffTime),
          cancelPendingLimitsTime: normalizeTimeValue(response.data.config.cancelPendingLimitsTime),
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Failed to update NSE market timing', { variant: 'error' });
    }
  };

  return (
    <Page title="NSE Market Timing">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="NSE Market Timing"
          links={[
            { name: 'Dashboard', href: `/${location.pathname.slice(1).split('/')[0]}` },
            { name: 'Settings' },
            { name: 'NSE Market Timing' },
          ]}
        />

        <Card sx={{ px: 3, py: 3 }}>
          {loading ? (
            <Stack spacing={2}>
              <Skeleton height={56} />
              <Skeleton height={56} />
              <Skeleton height={56} />
            </Stack>
          ) : (
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Stored on superAdmin config. Times are Asia/Kolkata (IST). Order must be: close only ≤ no
                new limits ≤ auto-cancel pending. Changing auto-cancel time also updates the cancel-nse-pending
                cron.
              </Alert>

              <Stack spacing={3} maxWidth={480}>
                <Box>
                  <RHFTextField
                    name="closeOnlyTime"
                    label="Close only from"
                    type="time"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    After this time until market close, only square-off of existing NSE positions is allowed. Limits may
                    only reduce existing positions.
                  </Typography>
                </Box>

                <Box>
                  <RHFTextField
                    name="limitCutoffTime"
                    label="No new/modify limits after"
                    type="time"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    After this time, new and modify limit orders for NSE are blocked. Market close-only square-offs remain
                    allowed until segment end.
                  </Typography>
                </Box>

                <Box>
                  <RHFTextField
                    name="cancelPendingLimitsTime"
                    label="Auto-cancel pending limits at"
                    type="time"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Cron runs Mon–Fri at this time to cancel remaining NSE pending limits.
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <LoadingButton variant="contained" color="primary" type="submit" loading={formState.isSubmitting}>
                  Update
                </LoadingButton>
              </Box>
            </FormProvider>
          )}
        </Card>
      </Container>
    </Page>
  );
};

export default NseMarketTimingSetting;
